import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlassCard, KpiTile as BrandKpiTile, LogoLockup, EnforcedText, BadgeMadeInBharat } from '../components/shared';
import LoadingSpinner from '../components/LoadingSpinner';
import { Book, FileText, Search, Download, Plus, CheckCircle, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Workbook = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [journey, setJourney] = useState(null);
  const [features, setFeatures] = useState([]);
  const [filteredFeatures, setFilteredFeatures] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [features, searchQuery, selectedCategory]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [journeyRes, featuresRes] = await Promise.all([
        fetch(`${API_URL}/api/workbook/journey`),
        fetch(`${API_URL}/api/workbook/features`)
      ]);

      const journeyData = await journeyRes.json();
      const featuresData = await featuresRes.json();

      setJourney(journeyData);
      setFeatures(featuresData.features || []);
    } catch (error) {
      console.error('Error fetching workbook data:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...features];

    if (selectedCategory !== 'All') {
      filtered = filtered.filter(f => f.category === selectedCategory);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(f =>
        f.name?.toLowerCase().includes(query) ||
        f.summary_md?.toLowerCase().includes(query) ||
        f.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }

    setFilteredFeatures(filtered);
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const response = await fetch(`${API_URL}/api/workbook/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ export_format: 'pdf', include_journey: true, include_features: true })
      });

      const data = await response.json();
      
      if (data.download_url) {
        window.open(data.download_url, '_blank');
      } else {
        alert('Export generated! Check exports center for download link.');
      }
    } catch (error) {
      console.error('Error exporting workbook:', error);
      alert('Error generating export');
    } finally {
      setExporting(false);
    }
  };

  const handleFeatureClick = (featureKey) => {
    navigate(`/workbook/feature/${featureKey}`);
  };

  if (loading) {
    return <LoadingSpinner size="lg" text="Loading Master Workbook..." />;
  }

  const categories = ['All', ...new Set(features.map(f => f.category))];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <LogoLockup size="md" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Master Workbook</h1>
            <p className="text-sm text-gray-500 mt-1">
              <EnforcedText>Strategy Journey + Feature Documentation System</EnforcedText>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExport}
            disabled={exporting}
            className="px-4 py-2 text-sm font-medium text-white bg-brand hover:opacity-90 rounded-2xl transition-opacity flex items-center gap-2 shadow-sm disabled:opacity-50"
          >
            <Download size={16} />
            {exporting ? 'Exporting...' : 'Export PDF+CSV'}
          </button>
        </div>
      </div>

      {/* Strategy Journey Band */}
      {journey && (
        <GlassCard
          title="Strategy Journey"
          subtitle="Goals → Roadmap → How URGAA Helps"
          footer="Definition: Platform strategy overview • Source: PMO • Updated: Live"
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Column A: Goals */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Column A: Goals</h3>
              <div className="space-y-3">
                {journey.goals?.map((goal) => (
                  <div key={goal.id} className="p-3 bg-purple-50 border border-purple-200 rounded-2xl">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg">{goal.icon === 'battery' ? '🔋' : goal.icon === 'map-path' ? '🗺️' : '🛡️'}</span>
                      <span className="font-medium text-sm text-gray-900">{goal.title}</span>
                    </div>
                    <p className="text-xs text-gray-600">{goal.one_liner || goal.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Column B: Roadmap */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Column B: Roadmap</h3>
              <div className="space-y-2">
                {journey.roadmap?.map((step, idx) => (
                  <div key={step.id}>
                    <div className="p-3 bg-green-50 border border-green-200 rounded-2xl">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-bold text-green-700">{idx + 1}</span>
                        <span className="font-medium text-sm text-gray-900">{step.title}</span>
                      </div>
                      {step.bullets && (
                        <ul className="text-xs text-gray-600 ml-4 list-disc">
                          {step.bullets.map((bullet, i) => (
                            <li key={i}>{bullet}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                    {idx < journey.roadmap.length - 1 && (
                      <div className="flex justify-center my-1">
                        <span className="text-gray-400">↓</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Column C: How URGAA Helps */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Column C: How URGAA Helps</h3>
              <div className="space-y-2">
                {journey.helps?.map((help) => (
                  <button
                    key={help.feature_key}
                    onClick={() => navigate(help.route)}
                    className="w-full px-3 py-2 text-left text-sm font-medium text-brand-purple bg-white border border-purple-200 hover:bg-purple-50 rounded-2xl transition-colors"
                  >
                    {help.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </GlassCard>
      )}

      {/* Feature Workbook Grid */}
      <div className="space-y-4">
        {/* Search & Filter Bar */}
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search features, tags, or keywords..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-purple"
            />
          </div>
          <div className="flex items-center gap-2">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 text-sm font-medium rounded-2xl transition-colors $
{
                  selectedCategory === cat
                    ? 'bg-brand text-white'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* KPI Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <BrandKpiTile
            title="Total Features"
            value={features.length}
            accentBar="primary"
            footer="Definition: Feature count • Source: Workbook • Updated: Live"
          />
          <BrandKpiTile
            title="Live Features"
            value={features.filter(f => f.status === 'Green' || f.status === 'Live').length}
            accentBar="green"
            footer="Definition: Production features • Source: Workbook • Updated: Live"
          />
          <BrandKpiTile
            title="In Progress"
            value={features.filter(f => f.status === 'Amber' || f.status === 'Beta').length}
            accentBar="bharat"
            footer="Definition: Beta features • Source: Workbook • Updated: Live"
          />
          <BrandKpiTile
            title="Filtered Results"
            value={filteredFeatures.length}
            accentBar="link"
            footer="Definition: Search results • Source: Client filter • Updated: Live"
          />
        </div>

        {/* Feature Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredFeatures.map((feature) => (
            <GlassCard
              key={feature.key}
              title={feature.name}
              subtitle={feature.category}
              footer={`Definition: ${feature.category} feature • Source: ${feature.owner} • Updated: ${new Date(feature.last_updated_at).toLocaleDateString()}`}
              onClick={() => handleFeatureClick(feature.key)}
              className="cursor-pointer hover:shadow-xl transition-all"
            >
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full $
{
                    feature.status === 'Green' ? 'bg-green-100 text-green-700' :
                    feature.status === 'Amber' ? 'bg-orange-100 text-orange-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {feature.status}
                  </span>
                </div>

                <p className="text-sm text-gray-700 line-clamp-3">{feature.summary_md}</p>

                <div className="flex flex-wrap gap-1">
                  {feature.tags?.slice(0, 3).map(tag => (
                    <span key={tag} className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>

                <button className="w-full px-4 py-2 text-sm font-medium text-white bg-brand hover:opacity-90 rounded-2xl transition-opacity flex items-center justify-center gap-2">
                  Open Feature
                  <FileText size={14} />
                </button>
              </div>
            </GlassCard>
          ))}
        </div>

        {filteredFeatures.length === 0 && (
          <div className="text-center py-12">
            <Book size={48} className="mx-auto mb-4 text-gray-400" />
            <p className="text-gray-500">No features found matching your search.</p>
          </div>
        )}
      </div>

      {/* Made in Bharat Badge */}
      <div className="flex justify-center">
        <BadgeMadeInBharat />
      </div>
    </div>
  );
};

export default Workbook;
